import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Envelope, Lock, User, Phone, WhatsappLogo, Gift } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LanguageSwitch from '../components/LanguageSwitch';
import SplashIntro from '../components/SplashIntro';
import SocialAuthRow from '../components/SocialAuthRow';
import WeeklyWashCounter from '../components/WeeklyWashCounter';
import { BRAND, BRAND_ASSETS } from '../config/programs';

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [splashKey, setSplashKey] = useState(() => Date.now());
  const [splashDone, setSplashDone] = useState(false);

  // Capture ?ref=CODE from referral links so the new user is bound to the inviter.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
      try { sessionStorage.setItem('autospa5-pending-ref', ref); } catch (err) { console.debug('[Register] sessionStorage unavailable', err?.message); }
    } else {
      try {
        const saved = sessionStorage.getItem('autospa5-pending-ref');
        if (saved) setReferralCode(saved);
      } catch (err) { console.debug('[Register] sessionStorage unavailable', err?.message); }
    }
  }, [location.search]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(form.email, form.password, form.name, form.phone, referralCode);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {!splashDone && (
        <SplashIntro key={splashKey} force onDone={() => setSplashDone(true)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 flex items-center justify-center">
            <div className="absolute inset-[-50%] rounded-full blur-2xl pointer-events-none"
                 style={{ background: `radial-gradient(circle, ${BRAND.primary} 0%, ${BRAND.primaryGlow} 40%, transparent 70%)`, opacity: 0.85 }} />
            <img src={BRAND_ASSETS.logo} alt="AutoSpa5"
                 className="w-12 h-12 relative z-10 object-contain"
                 style={{ filter: `drop-shadow(0 0 8px ${BRAND.primary}) drop-shadow(0 0 20px ${BRAND.primary}) drop-shadow(0 0 36px ${BRAND.primaryGlow})` }} />
          </div>
          <span className="text-xl font-semibold tracking-tight font-['Outfit']">AutoSpa5</span>
        </div>
        <LanguageSwitch />
      </div>

      {/* Content (no big centered logo — the header logo is the only one) */}
      <motion.div 
        className="flex-1 flex flex-col justify-center px-6 pb-8"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: splashDone ? 1 : 0, y: splashDone ? 0 : 24 }}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        <div className="max-w-sm mx-auto w-full">
          <h1 className="text-3xl font-medium tracking-tight mb-2 font-['Outfit']">{t('register')}</h1>
          <p className="text-[#0ABAB5] font-medium mb-1">{t('mainSlogan')}</p>
          <p className="text-white/50 text-sm">{t('washIn5Min')}</p>

          <WeeklyWashCounter />

          <form onSubmit={handleSubmit} className="space-y-3 mt-6">
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="relative">
              <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="text"
                name="name"
                placeholder={t('name')}
                value={form.name}
                onChange={handleChange}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="register-name"
                required
              />
            </div>

            <div className="relative">
              <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="email"
                name="email"
                placeholder={t('email')}
                value={form.email}
                onChange={handleChange}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="register-email"
                required
              />
            </div>

            <div className="relative">
              <WhatsappLogo size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#25D366]" />
              <Input
                type="tel"
                name="phone"
                placeholder={t('whatsappNumber')}
                value={form.phone}
                onChange={handleChange}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="register-phone"
              />
            </div>
            <p className="text-[10px] text-white/40 -mt-2 ml-1">{t('whatsappRecoveryHint')}</p>

            <div className="relative">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="password"
                name="password"
                placeholder={t('password')}
                value={form.password}
                onChange={handleChange}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="register-password"
                required
              />
            </div>

            <div className="relative">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                type="password"
                name="confirmPassword"
                placeholder={t('confirmPassword')}
                value={form.confirmPassword}
                onChange={handleChange}
                className="h-14 bg-[#121212] border-white/10 pl-12 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5] focus:ring-[#0ABAB5]"
                data-testid="register-confirm-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 cta-button-orange font-semibold rounded-2xl mt-2"
              data-testid="register-submit"
            >
              {loading ? t('loading') : t('signUp')}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/40 text-sm">{t('continueWith')}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <SocialAuthRow
            onGoogle={loginWithGoogle}
            onMock={(p) => setError(`${p} signup is not yet available. Please use email or Google.`)}
          />

          <p className="mt-6 text-center text-white/50">
            {t('haveAccount')}{' '}
            <Link
              to="/login"
              onClick={(e) => {
                e.preventDefault();
                try { sessionStorage.removeItem('autospa5-splash-played'); } catch (err) { console.debug('[Register] sessionStorage unavailable', err?.message); }
                setSplashDone(false);
                setSplashKey(Date.now());
                navigate('/login');
              }}
              className="text-[#0ABAB5] font-medium"
              data-testid="login-link"
            >
              {t('signIn')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
